import type { AABB3 } from "geom/aabb3Types";
import type { Vector3 } from "three";
import { EventDispatcher } from "three";
import type { THREECamera } from "./THREECamera";

/**
 * Base class for canvas-based controls. Provides EventDispatcher inheritance and
 * the center/domElement getters/setters from the previous prototype setup.
 */
export abstract class CanvasControls extends EventDispatcher<{ change: {}; projectionChange: {}; start: {}; end: {} }> {
  protected _domElement: HTMLElement | Document;

  readonly #lockedBy: Set<string> = new Set();

  autoRotate: boolean = false;
  autoRotateSpeed: number = 2.0;
  enablePan: boolean = true;
  enableRotate: boolean = true;
  enableZoom: boolean = true;
  ignoreLeftClickWithoutCtrl: boolean = false;
  maxDistance: number = Infinity;
  minDistance: number = 0;
  minPolarAngle: number = 0;
  maxPolarAngle: number = Math.PI;
  minAzimuthAngle: number = -Infinity;
  maxAzimuthAngle: number = Infinity;
  minZoom: number = 0;
  maxZoom: number = Infinity;
  panContainBox: AABB3 | null = null;
  panSpeed: number = 1.0;
  rotateSpeed: number = 1.0;
  screenSpacePanning: boolean = true;
  zoomSpeed: number = 1.0;

  enableDamping: boolean = false;
  dampingFactor: number = 0.25;

  constructor(
    domElement: HTMLElement | Document,
    public object: THREECamera,
    public target: Vector3,
  ) {
    super();
    this._domElement = domElement;
  }

  get enabled(): boolean {
    return this.#lockedBy.size === 0;
  }

  get lockedBy(): ReadonlySet<string> {
    return this.#lockedBy;
  }

  lock(id: string): void {
    this.#lockedBy.add(id);
  }

  unlock(id: string): void {
    this.#lockedBy.delete(id);
  }

  get domElement(): HTMLElement | Document {
    return this._domElement;
  }

  set domElement(domElement: HTMLElement | Document | undefined | null) {
    this.removeDomElementListeners();
    this._domElement = domElement ?? document;
    this.addDomElementListeners();
  }

  abstract addDomElementListeners(): void;
  abstract removeDomElementListeners(): void;
  abstract update(): void;
  abstract dispose(): void;
  abstract zoomTowardsCentre(): void;
  abstract zoomOutFromCentre(): void;

  getMinMaxDistance(): number {
    return 0;
  }

  protected getAutoRotationAngle(): number {
    return ((2 * Math.PI) / 60 / 60) * this.autoRotateSpeed;
  }
}
